/**
 * @fileoverview Day-one "primary contact" person resolution (MOR-2342).
 *
 * The `contact_*` canonical fields (contact person name, email, phone) feed
 * letters and the insurer's "IP file manager or primary contact" block. A
 * firm chooses who they default to — the Contact preset, the analyst
 * preparing the documents, the lead appointee, or a nominated staff member —
 * and the analyst can switch per generation. This module turns that choice
 * into the field values; it is pure so the wizard and tests share it.
 *
 * Precedence is unchanged: these values sit ABOVE the firm's Contact preset
 * and BELOW a per-document edit (they are merged into the org-defaults layer
 * of `mergeFieldValues`).
 */

import type { DayOneContactDefaultMode } from "@cvx/organizations/schema";

/** A firm member (or the appointee) as a contact person. */
export interface ContactPersonProfile {
  name: string;
  email?: string;
  phone?: string;
}

export interface ResolveContactPersonArgs {
  mode: DayOneContactDefaultMode;
  /** The analyst generating the documents. */
  preparingUser?: ContactPersonProfile | null;
  /** The nominated staff member when `mode` is `staff_member`. */
  staffMember?: ContactPersonProfile | null;
  /** The member assigned to the chosen workflow role on this job (`job_role`). */
  jobRoleMember?: ContactPersonProfile | null;
  /** The lead appointee's printed name (the `practitioner_name` field). */
  appointeeName?: string | null;
  /** Firm members, used to look up the appointee's own email / phone. */
  members?: ReadonlyArray<ContactPersonProfile>;
}

export const CONTACT_PERSON_MODE_LABELS: Record<
  DayOneContactDefaultMode,
  string
> = {
  firm_preset: "Contact Preset",
  preparing_user: "Person Preparing the Documents",
  appointee: "Lead Appointee",
  staff_member: "Nominated Staff Member",
  job_role: "Person Allocated to a Job Role",
};

/** Display name for a firm member: first + last, else email. */
export function memberDisplayName(user: {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}): string {
  return (
    [user.firstName, user.lastName]
      .map((part) => part?.trim())
      .filter((part): part is string => !!part)
      .join(" ") || user.email
  );
}

/**
 * The `contact_*` field values for the chosen contact person. Returns an
 * empty object for `firm_preset` (nothing to override) and when the chosen
 * person cannot be resolved, so the preset / fallbacks apply as before.
 * Only present details are emitted — a member without a phone leaves
 * `contact_phone` to the preset or the firm switchboard fallback.
 *
 * @example
 * resolveContactPersonFields({ mode: "preparing_user", preparingUser: { name: "Oscar Bush", email: "o@wlpr.com.au", phone: "02 8365 1416" } });
 * // { contact_name: "Oscar Bush", contact_email: "o@wlpr.com.au", contact_phone: "02 8365 1416" }
 */
export function resolveContactPersonFields(
  args: ResolveContactPersonArgs,
): Record<string, string> {
  const profile = contactPersonProfile(args);
  if (!profile) return {};
  const entries: Array<[string, string | undefined]> = [
    ["contact_name", profile.name],
    ["contact_email", profile.email],
    ["contact_phone", profile.phone],
  ];
  return Object.fromEntries(
    entries.filter((entry): entry is [string, string] => !!entry[1]?.trim()),
  );
}

function contactPersonProfile(
  args: ResolveContactPersonArgs,
): ContactPersonProfile | null {
  switch (args.mode) {
    case "firm_preset":
      return null;
    case "preparing_user":
      return args.preparingUser ?? null;
    case "staff_member":
      return args.staffMember ?? null;
    case "job_role":
      return args.jobRoleMember ?? null;
    case "appointee": {
      const name = args.appointeeName?.trim();
      if (!name) return null;
      const member = (args.members ?? []).find(
        (m) => m.name.trim().toLowerCase() === name.toLowerCase(),
      );
      return member ?? { name };
    }
    default: {
      const exhaustive: never = args.mode;
      return exhaustive;
    }
  }
}
